/**
 * Registration form state management using TC39 Signals.
 */
import { Signal } from 'signal-polyfill';
import type { EntryPricingQuoteDto, RunSelectionsDto } from '../api/types';
import type { RunSelectionDraft } from '../services/run-selections';

export interface DogFields {
  ascaRegistrationNumber: string;
  breed: string;
  registeredName: string;
  callName: string;
  dob: string;
  color: string;
  sex: string;
  sire: string;
  dam: string;
  breeders: string;
}

export interface OwnerAddressFields {
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface JuniorHandlerFields {
  dob: string;
  memberId: string;
}

export interface ContactFields {
  owners: string;
  ownerAddress: OwnerAddressFields;
  ownerEmail: string;
  phone: string;
  handler: string;
  handlerEmail: string;
  membershipNumber: string;
  junior: JuniorHandlerFields;
}

export type ContactUpdate = Omit<Partial<ContactFields>, 'ownerAddress' | 'junior'> & {
  ownerAddress?: Partial<OwnerAddressFields>;
  junior?: Partial<JuniorHandlerFields>;
};

export interface EmergencyContactFields {
  name: string;
  phoneOrNumber: string;
}

export interface FeesFields {
  totalEntryFees: string;
  currency: string;
}

export interface PremiumFields {
  acknowledged: boolean;
  signature: string;
}

export interface TermsFields {
  accepted: boolean;
  version: string;
}

export interface GridSelection {
  row: string;
  col: string;
  value: string | null;
}

export interface ValidationError {
  field: string;
  message: string;
  code?: string;
}

export interface FormStateShape {
  trialEventId: string | null;
  entryId: string | null;
  entryEtag: string | null;
  dog: DogFields;
  contact: ContactFields;
  emergencyContact: EmergencyContactFields;
  fees: FeesFields;
  premium: PremiumFields;
  terms: TermsFields;
  selections: GridSelection[];
  runSelections: RunSelectionsDto | null;
  runDraft: RunSelectionDraft | null;
  entryRevision: number | null;
  pricingQuote: EntryPricingQuoteDto | null;
  isDirty: boolean;
  validationErrors: ValidationError[];
  isSubmitting: boolean;
  isSaving: boolean;
  isValidationStale: boolean;
}

const initialDogFields: DogFields = {
  ascaRegistrationNumber: '',
  breed: '',
  registeredName: '',
  callName: '',
  dob: '',
  color: '',
  sex: '',
  sire: '',
  dam: '',
  breeders: '',
};

const initialOwnerAddress: OwnerAddressFields = {
  street: '',
  city: '',
  state: '',
  zip: '',
};

const initialJuniorHandler: JuniorHandlerFields = {
  dob: '',
  memberId: '',
};

const initialContactFields: ContactFields = {
  owners: '',
  ownerAddress: initialOwnerAddress,
  ownerEmail: '',
  phone: '',
  handler: '',
  handlerEmail: '',
  membershipNumber: '',
  junior: initialJuniorHandler,
};

const initialEmergencyContact: EmergencyContactFields = {
  name: '',
  phoneOrNumber: '',
};

const initialFees: FeesFields = {
  totalEntryFees: '',
  currency: 'USD',
};

const initialPremium: PremiumFields = {
  acknowledged: false,
  signature: '',
};

const initialTerms: TermsFields = {
  accepted: false,
  version: '',
};

const initialFormState: FormStateShape = {
  trialEventId: null,
  entryId: null,
  entryEtag: null,
  dog: initialDogFields,
  contact: initialContactFields,
  emergencyContact: initialEmergencyContact,
  fees: initialFees,
  premium: initialPremium,
  terms: initialTerms,
  selections: [],
  runSelections: null,
  runDraft: null,
  entryRevision: null,
  pricingQuote: null,
  isDirty: false,
  validationErrors: [],
  isSubmitting: false,
  isSaving: false,
  isValidationStale: true,
};

/**
 * Reactive form state signal.
 */
export const formState = new Signal.State<FormStateShape>(initialFormState);

/**
 * Computed signal: is the form valid (no validation errors)
 */
export const isFormValid = new Signal.Computed(() => {
  const state = formState.get();
  return state.validationErrors.length === 0;
});

/**
 * Computed signal: count of selected grid cells
 */
export const selectedCount = new Signal.Computed(() => {
  const state = formState.get();
  return state.runDraft?.runs.length ?? state.runSelections?.runs.length ?? 0;
});

/**
 * Computed signal: has any selections
 */
export const hasSelections = new Signal.Computed(() => {
  return selectedCount.get() > 0;
});

/**
 * Computed signal: is form busy (submitting or saving)
 */
export const isFormBusy = new Signal.Computed(() => {
  const state = formState.get();
  return state.isSubmitting || state.isSaving;
});

/**
 * Computed signal: is validation stale (needs re-validation)
 */
export const isValidationStale = new Signal.Computed(() => {
  return formState.get().isValidationStale;
});

/**
 * Form state management utilities
 */
export const formActions = {
  /**
   * Initialize form for a trial event.
   */
  initializeForTrial(trialEventId: string): void {
    formState.set({
      ...initialFormState,
      trialEventId,
    });
  },

  /**
   * Load existing entry data into form.
   */
  loadEntry(entryId: string, data: Partial<FormStateShape>): void {
    const current = formState.get();
    formState.set({
      ...current,
      ...data,
      entryId,
      isDirty: false,
    });
  },

  /**
   * Store the latest draft ETag for optimistic concurrency.
   */
  setEntryEtag(entryEtag: string | null): void {
    const current = formState.get();
    formState.set({
      ...current,
      entryEtag,
    });
  },

  /**
   * Update dog fields.
   */
  updateDog(updates: Partial<DogFields>): void {
    const current = formState.get();
    formState.set({
      ...current,
      dog: { ...current.dog, ...updates },
      isDirty: true,
      isValidationStale: true,
    });
  },

  /**
   * Update contact fields.
   */
  updateContact(updates: ContactUpdate): void {
    const current = formState.get();
    const ownerAddress = updates.ownerAddress
      ? { ...current.contact.ownerAddress, ...updates.ownerAddress }
      : current.contact.ownerAddress;
    const junior = updates.junior
      ? { ...current.contact.junior, ...updates.junior }
      : current.contact.junior;
    formState.set({
      ...current,
      contact: { ...current.contact, ...updates, ownerAddress, junior },
      pricingQuote: updates.junior ? null : current.pricingQuote,
      isDirty: true,
      isValidationStale: true,
    });
  },

  /**
   * Update emergency contact fields.
   */
  updateEmergencyContact(updates: Partial<EmergencyContactFields>): void {
    const current = formState.get();
    formState.set({
      ...current,
      emergencyContact: { ...current.emergencyContact, ...updates },
      isDirty: true,
      isValidationStale: true,
    });
  },

  /**
   * Update fee fields.
   */
  updateFees(updates: Partial<FeesFields>): void {
    const current = formState.get();
    formState.set({
      ...current,
      fees: { ...current.fees, ...updates },
      isDirty: true,
      isValidationStale: true,
    });
  },

  /**
   * Update terms fields.
   */
  updateTerms(updates: Partial<TermsFields>): void {
    const current = formState.get();
    formState.set({
      ...current,
      terms: { ...current.terms, ...updates },
      isDirty: true,
      isValidationStale: true,
    });
  },

  /**
   * Update grid selections.
   */
  updateSelections(selections: GridSelection[]): void {
    const current = formState.get();
    formState.set({
      ...current,
      selections,
      isDirty: true,
      isValidationStale: true,
    });
  },

  updateRunSelections(runDraft: RunSelectionDraft): void {
    formState.set({
      ...formState.get(), runDraft, pricingQuote: null,
      isDirty: true, isValidationStale: true,
    });
  },

  setPricingQuote(pricingQuote: EntryPricingQuoteDto | null): void {
    formState.set({ ...formState.get(), pricingQuote });
  },

  /**
   * Set validation errors.
   */
  setValidationErrors(errors: ValidationError[]): void {
    const current = formState.get();
    formState.set({
      ...current,
      validationErrors: errors,
    });
  },

  /**
   * Clear all validation errors.
   */
  clearValidationErrors(): void {
    const current = formState.get();
    formState.set({
      ...current,
      validationErrors: [],
    });
  },

  /**
   * Set submitting state.
   */
  setSubmitting(isSubmitting: boolean): void {
    const current = formState.get();
    formState.set({
      ...current,
      isSubmitting,
    });
  },

  /**
   * Set saving state.
   */
  setSaving(isSaving: boolean): void {
    const current = formState.get();
    formState.set({
      ...current,
      isSaving,
    });
  },

  /**
   * Mark form as clean (not dirty).
   */
  markClean(): void {
    const current = formState.get();
    formState.set({
      ...current,
      isDirty: false,
    });
  },

  /**
   * Update premium fields.
   */
  updatePremium(updates: Partial<PremiumFields>): void {
    const current = formState.get();
    formState.set({
      ...current,
      premium: { ...current.premium, ...updates },
      isDirty: true,
      isValidationStale: true,
    });
  },

  /**
   * Mark validation as fresh (not stale).
   */
  markValidationFresh(): void {
    const current = formState.get();
    formState.set({
      ...current,
      isValidationStale: false,
    });
  },

  /**
   * Mark validation as stale (needs re-validation).
   */
  markValidationStale(): void {
    const current = formState.get();
    formState.set({
      ...current,
      isValidationStale: true,
    });
  },

  /**
   * Reset form to initial state.
   */
  reset(): void {
    formState.set(initialFormState);
  },
};
